import { __decorate } from "tslib";
import { Group, Vector3 } from "three";
import { CrossGoniometerConfig as Config } from "../Config/CrossGoniometerConfig.js";
import { ToolType } from "../Core/ITool.js";
import { Save, SaveChild } from "../Core/Save.js";
import { ViewType } from "../Models/AppModel.js";
import { Consts } from "../Utils/Consts.js";
import { Default } from "../Utils/Default.js";
import { FormatUtils } from "../Utils/FormatUtils.js";
import { DraggablePoint } from "./Primitive/DraggablePoint.js";
import { LabelAngle } from "./Primitive/Label/LabelAngle.js";
import { LabelText } from "./Primitive/Label/LabelText.js";
import { LinePointToPoint2 } from "./Primitive/LinePointToPoint2.js";
import { isBitSet } from "../Utils/Utils.js";
import { StateTypes } from "../States/State.js";
/**
 * Cross goniometer mesh with angles and lengths labels.
 */
export class CrossGoniometer extends Group {
    onSelect() {
        Default.colorAllDraggablePoints(this);
    }
    onDeselect() {
        Default.restoreColorAllDraggablePoints(this);
    }
    constructor(viewType, index, A, B, C, D) {
        super();
        this.toolType = ToolType.goniometer;
        this.isSelectable = true;
        this.isDeletable = true;
        this.isSelected = false;
        this._pan = new Vector3();
        this.onAfterRestore = () => {
            this.update();
        };
        this.name = "CrossGoniometer";
        this.position.z = 10;
        this.add(this.A = new DraggablePoint("A", viewType, Config.A_color, A), this.B = new DraggablePoint("B", viewType, Config.B_color, B), this.C = new DraggablePoint("C", viewType, Config.C_color, C), this.D = new DraggablePoint("D", viewType, Config.D_color, D), this.center = new DraggablePoint("center", viewType, Config.center_color, this.A.position.clone().lerp(this.C.position, 0.5)), this.ACLine = new LinePointToPoint2("ACLine", viewType, Config.ACLine_color, this.A, this.C), this.BDLine = new LinePointToPoint2("BDLine", viewType, Config.BDLine_color, this.B, this.D), this.lengthACLabel = new LabelText({
            name: 'lengthACLabel',
            viewType,
            objToAnchor: this.A,
            anchor: this.C.position.clone().sub(this.A.position).setLength(10),
            color: 0xFF4DDD, //todo conf
            labelParams: { backgroundColor: Config.lengthBDLabel_backgroundColor, fontColor: Config.lengthACLabel_fontColor }
        }), this.lengthBDLabel = new LabelText({
            name: 'lengthBDLabel',
            viewType,
            objToAnchor: this.B,
            anchor: this.D.position.clone().sub(this.B.position).setLength(10),
            color: 0xFF4DDD, //todo conf
            labelParams: { backgroundColor: Config.lengthBDLabel_backgroundColor, fontColor: Config.lengthACLabel_fontColor }
        }), this.angleABLabel = new LabelAngle({
            name: 'angleACLabel',
            arcA: this.A,
            arcB: this.B,
            arcCenter: this.center,
            viewType: viewType,
            placeOnBisector: true,
            labelParams: { backgroundColor: Config.lengthBDLabel_backgroundColor, fontColor: Config.lengthACLabel_fontColor }
        }), this.angleBCLabel = new LabelAngle({
            name: 'angleBCLabel',
            arcA: this.B,
            arcB: this.C,
            arcCenter: this.center,
            viewType: viewType,
            placeOnBisector: true,
            labelParams: { backgroundColor: Config.lengthBDLabel_backgroundColor, fontColor: Config.lengthACLabel_fontColor }
        }));
        this.update();
        this.bindEvents();
        const isRegisteringClick = viewType === ViewType.AP ? "isRegisteringClickAP" : "isRegisteringClickLT";
        const mask = viewType === ViewType.AP ? "goniometersMaskAP" : "goniometersMaskLT";
        this.bindProperty("visible", (m) => {
            return !m[isRegisteringClick] && m.layer_goniometers && isBitSet(m[mask], index) && m.appState > StateTypes.calibrated && !m.isViewPlanPrinting;
        }, [isRegisteringClick, 'layer_goniometers', mask, 'appState', 'isViewPlanPrinting']);
        this.bindProperty("isEnabled", (m) => { return !m.readonly; }, ['readonly']);
    }
    /**
     * Gets the points to init goniometer to the camera center.
     */
    static getPoints(cameraCenter) {
        const dir = Consts.verDir.clone().setLength(50);
        const dirPerp = Consts.horDir.clone().setLength(50);
        return {
            A: cameraCenter.clone().setZ(3).add(dir),
            B: cameraCenter.clone().setZ(3).add(dirPerp),
            C: cameraCenter.clone().setZ(3).sub(dir),
            D: cameraCenter.clone().setZ(3).sub(dirPerp)
        };
    }
    get distanceAC() {
        return this.A.position.distanceTo(this.C.position);
    }
    get distanceBD() {
        return this.B.position.distanceTo(this.D.position);
    }
    bindEvents() {
        this.bindEvent("onAfterDragMove", () => {
            this.update();
        });
        this.A.bindEvent("onDragMove", (args) => {
            const length = this.C.position.distanceTo(this.center.position);
            this.C.position.copy(this.center.position.clone().add(this.center.position.clone().sub(args.position).setLength(length)));
        });
        this.B.bindEvent("onDragMove", (args) => {
            const length = this.D.position.distanceTo(this.center.position);
            this.D.position.copy(this.center.position.clone().add(this.center.position.clone().sub(args.position).setLength(length)));
        });
        this.C.bindEvent("onDragMove", (args) => {
            const length = this.A.position.distanceTo(this.center.position);
            this.A.position.copy(this.center.position.clone().add(this.center.position.clone().sub(args.position).setLength(length)));
        });
        this.D.bindEvent("onDragMove", (args) => {
            const length = this.B.position.distanceTo(this.center.position);
            this.B.position.copy(this.center.position.clone().add(this.center.position.clone().sub(args.position).setLength(length)));
        });
        this.center.bindEvent("onDragMove", (args) => {
            this._pan.copy(this.center.position).sub(args.position);
            this.A.position.sub(this._pan);
            this.B.position.sub(this._pan);
            this.C.position.sub(this._pan);
            this.D.position.sub(this._pan);
        });
    }
    update() {
        this.lengthACLabel.setParam("text", FormatUtils.distanceWithSymbol(this.distanceAC));
        this.lengthBDLabel.setParam("text", FormatUtils.distanceWithSymbol(this.distanceBD));
    }
    dispose() {
        super.dispose();
        this.A.dispose();
        this.B.dispose();
        this.C.dispose();
        this.D.dispose();
        this.ACLine.dispose(); // LinePointToPoint2;
        this.BDLine.dispose();
        this.lengthACLabel.dispose();
        this.lengthBDLabel.dispose();
        this.angleABLabel.dispose(); // label
        this.angleBCLabel.dispose();
        this.center.dispose();
    }
    setPoints(A, B, C, D) {
        this.A.position.copy(A);
        this.B.position.copy(B);
        this.C.position.copy(C);
        this.D.position.copy(D);
        this.center.position.copy(this.A.position.clone().lerp(this.C.position, 0.5));
        this.update();
    }
}
__decorate([
    Save("position")
], CrossGoniometer.prototype, "A", void 0);
__decorate([
    Save("position")
], CrossGoniometer.prototype, "B", void 0);
__decorate([
    Save("position")
], CrossGoniometer.prototype, "C", void 0);
__decorate([
    Save("position")
], CrossGoniometer.prototype, "D", void 0);
__decorate([
    Save("position")
], CrossGoniometer.prototype, "center", void 0);
__decorate([
    SaveChild()
], CrossGoniometer.prototype, "lengthACLabel", void 0);
__decorate([
    SaveChild()
], CrossGoniometer.prototype, "lengthBDLabel", void 0);
__decorate([
    Save()
], CrossGoniometer.prototype, "_pan", void 0);
//# sourceMappingURL=CrossGoniometer.js.map